// Motor de animación puro de la pizarra.
// Dado un BoardJson + timeline y un instante t, calcula la posición de cada
// elemento animable (balón siguiendo pases, jugadores siguiendo movimientos).
// Es una función pura sin dependencias de Konva, por lo que se puede testear
// con Vitest (ver board-animation.spec.ts). El playback (Fase 1b) la envolverá
// con Konva.Animation en el componente de la pizarra.

import { ARROW_KEYS, BoardArrow, BoardJson, BoardTimeline, TimelineEvent, TimelineEventType } from '../interfaces/board-json.interface';
import { ENABLE_PLAYER_ROTATION_DURING_PLAYBACK } from '../components/konva-board/konva-board.constants';
import { Court } from './court';

/** Posición en el plano de la pizarra. */
export interface FramePosition {
  x: number;
  y: number;
}

/** Curva de easing temporal: mapea el progreso local 0..1 a 0..1. */
export type EasingFn = (t: number) => number;

export interface AnimationOptions {
  /** Curva de easing temporal. Por defecto 'linear'. */
  easing?: EasingFn;
}

/** Resultado de evaluar la animación en un instante t. */
export interface BoardFrame {
  /** Posición de cada elemento, indexada por su id (jugadores, defensores, balones). */
  positions: Map<string, FramePosition>;
  /** Rotación (radianes) de cada elemento en el instante t. Solo se puebla en
   *  concatenaciones (rotationKeyframes); sin ellas queda vacío y cada
   *  elemento conserva su rotación cargada. */
  rotations: Map<string, number>;
  /** Elementos visibles (ids) en el instante t. Solo se puebla en
   *  concatenaciones (elementVisibility); sin ellas queda vacío y todos los
   *  elementos permanecen visibles. */
  visibleElements: Set<string>;
  /** Rol de cada jugador/defensor en el instante t ('player' o 'defender').
   *  Se puebla siempre: rol base del BoardJson + eventos de tipo 'role' con
   *  startTime <= t (el último gana; el cambio es instantáneo). */
  roles: Map<string, 'player' | 'defender'>;
  /** Progreso global 0..1 dentro de la línea de tiempo. */
  progress: number;
  /** Eventos activos en el instante t, ordenados por startTime. */
  activeEvents: TimelineEvent[];
}

const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Radios de los elementos para calcular el borde donde se coloca el balón. */
const PLAYER_RADIUS = 14;
const DEFENDER_RADIUS = 12;
const BALL_RADIUS = 8;

/** Easing lineal (identidad). */
export const linear: EasingFn = (t) => t;

/** Easing ease-in-out cuadrático: suave al inicio y al final. */
export const easeInOut: EasingFn = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

/**
 * Punto sobre la trayectoria de una flecha según el progreso 0..1.
 * Soporta flechas rectas, curvas (cx/cy) y trazo libre (points).
 */
export function pointOnArrow(arrow: BoardArrow, progress: number): FramePosition {
  const t = clamp01(progress);
  if (arrow.points && arrow.points.length >= 4) {
    return pointOnPolyline(arrow.points, t);
  }
  if (arrow.cx !== undefined && arrow.cy !== undefined) {
    const u = 1 - t;
    return {
      x: u * u * arrow.x1 + 2 * u * t * arrow.cx + t * t * arrow.x2,
      y: u * u * arrow.y1 + 2 * u * t * arrow.cy + t * t * arrow.y2,
    };
  }
  return { x: lerp(arrow.x1, arrow.x2, t), y: lerp(arrow.y1, arrow.y2, t) };
}

/** Punto sobre una polilínea de puntos [x0,y0,x1,y1,...] según el progreso 0..1. */
function pointOnPolyline(points: number[], t: number): FramePosition {
  const segments: { start: FramePosition; end: FramePosition; length: number }[] = [];
  let total = 0;
  for (let i = 0; i + 3 < points.length; i += 2) {
    const start = { x: points[i], y: points[i + 1] };
    const end = { x: points[i + 2], y: points[i + 3] };
    const length = Math.hypot(end.x - start.x, end.y - start.y);
    segments.push({ start, end, length });
    total += length;
  }
  if (total === 0) return { x: points[0], y: points[1] };

  let target = t * total;
  for (const seg of segments) {
    if (target <= seg.length) {
      const local = seg.length > 0 ? target / seg.length : 0;
      return { x: lerp(seg.start.x, seg.end.x, local), y: lerp(seg.start.y, seg.end.y, local) };
    }
    target -= seg.length;
  }
  const last = segments[segments.length - 1];
  return last ? { ...last.end } : { x: points[0], y: points[1] };
}

/**
 * Calcula el ángulo (en radianes) para rotar un jugador/defensor de modo que
 * el número quede orientado con su parte superior hacia la cola de la flecha
 * (atrás) y la inferior hacia la punta (adelante).
 * El ángulo base es la tangente de la flecha; se resta PI/2 porque en el
 * renderizado el número está "erguido" (arriba = -Y) y queremos que su "arriba"
 * apunte al vector contrario a la dirección de la flecha.
 */
export function angleOnArrow(arrow: BoardArrow, progress: number): number {
  const t = clamp01(progress);
  let baseAngle: number;
  if (arrow.points && arrow.points.length >= 4) {
    baseAngle = angleOnPolyline(arrow.points, t);
  } else if (arrow.cx !== undefined && arrow.cy !== undefined) {
    // Derivada de bezier cuadrático: B'(t) = 2(1-t)(P1-P0) + 2t(P2-P1)
    const u = 1 - t;
    const dx = 2 * u * (arrow.cx - arrow.x1) + 2 * t * (arrow.x2 - arrow.cx);
    const dy = 2 * u * (arrow.cy - arrow.y1) + 2 * t * (arrow.y2 - arrow.cy);
    baseAngle = Math.atan2(dy, dx);
  } else {
    // Flecha recta: ángulo constante
    baseAngle = Math.atan2(arrow.y2 - arrow.y1, arrow.x2 - arrow.x1);
  }
  // Offset -PI/2: el número "mira" hacia atrás de la flecha (cola)
  return baseAngle - Math.PI / 2;
}

/** Ángulo de la tangente en una polilínea según el progreso 0..1. */
function angleOnPolyline(points: number[], t: number): number {
  const segments: { start: FramePosition; end: FramePosition; length: number; angle: number }[] = [];
  let total = 0;
  for (let i = 0; i + 3 < points.length; i += 2) {
    const start = { x: points[i], y: points[i + 1] };
    const end = { x: points[i + 2], y: points[i + 3] };
    const length = Math.hypot(end.x - start.x, end.y - start.y);
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    segments.push({ start, end, length, angle });
    total += length;
  }
  if (total === 0) return 0;

  let target = t * total;
  for (const seg of segments) {
    if (target <= seg.length) {
      return seg.angle;
    }
    target -= seg.length;
  }
  const last = segments[segments.length - 1];
  return last ? last.angle : 0;
}

/** Posición inicial de los elementos animables (jugadores, defensores, balones). */
function initialPositions(json: BoardJson): Map<string, FramePosition> {
  const map = new Map<string, FramePosition>();
  const add = (id: string, x: number, y: number): void => {
    if (id) map.set(id, { x, y });
  };
  json.players.forEach((p) => add(p.id, p.x, p.y));
  json.defenders.forEach((d) => add(d.id, d.x, d.y));
  json.balls.forEach((b) => add(b.id, b.x, b.y));
  return map;
}

/** Busca una flecha por su id en todas las listas del BoardJson. */
function findArrow(json: BoardJson, arrowId?: string | null): BoardArrow | null {
  if (!arrowId) return null;
  for (const key of ARROW_KEYS) {
    const found = (json[key] as BoardArrow[]).find((a) => a.id === arrowId);
    if (found) return found;
  }
  return null;
}

/** Verifica si un id corresponde a un jugador o defensor. */
function isPerson(json: BoardJson, id: string): boolean {
  return json.players.some((p) => p.id === id) || json.defenders.some((d) => d.id === id);
}

/**
 * Id del balón asociado a una flecha de pase/tiro/bote: el balón del que
 * arranca la flecha (sourceId), el único balón del tablero o, con varios
 * balones, el más cercano al jugador que pasa (sourceId si es persona) o
 * al inicio de la flecha. Sin esto, con más de un balón la animación no
 * sabría cuál mover.
 */
function resolveArrowBallId(json: BoardJson, arrow: BoardArrow, positions: Map<string, FramePosition>): string | null {
  // 1. Si la flecha arranca desde un balón (sourceId es un balón), usar ese balón.
  if (arrow.sourceId && json.balls.some((b) => b.id === arrow.sourceId)) return arrow.sourceId;
  // 2. Si solo hay un balón, usar ese.
  if (json.balls.length === 1) return json.balls[0].id;
  // 3. Si la flecha tiene sourceId que es un jugador/defensor, buscar el balón
  //    más cercano a ESE jugador (no al inicio geométrico de la flecha, que
  //    puede no coincidir exactamente con la posición del jugador).
  if (arrow.sourceId && isPerson(json, arrow.sourceId)) {
    const passerPos = positions.get(arrow.sourceId);
    if (passerPos) {
      let closestId: string | null = null;
      let minDistSq = Infinity;
      for (const b of json.balls) {
        const ballPos = positions.get(b.id);
        if (!ballPos) continue;
        const dx = ballPos.x - passerPos.x;
        const dy = ballPos.y - passerPos.y;
        const d = dx * dx + dy * dy;
        if (d < minDistSq) {
          minDistSq = d;
          closestId = b.id;
        }
      }
      return closestId;
    }
  }
  // 4. Fallback: balón más cercano al inicio geométrico de la flecha.
  let closestId: string | null = null;
  let minDistSq = Infinity;
  for (const b of json.balls) {
    const pos = positions.get(b.id);
    if (!pos) continue;
    const dx = pos.x - arrow.x1;
    const dy = pos.y - arrow.y1;
    const d = dx * dx + dy * dy;
    if (d < minDistSq) {
      minDistSq = d;
      closestId = b.id;
    }
  }
  return closestId;
}

/** Id del balón que mueve un evento de pase/tiro/bote (ballId o el resuelto de la flecha). */
function resolveBallId(json: BoardJson, event: TimelineEvent, segmentInitial: Map<string, FramePosition>): string | null {
  if (event.ballId) return json.balls.some((b) => b.id === event.ballId) ? event.ballId : null;
  const arrow = findArrow(json, event.arrowId);
  return arrow ? resolveArrowBallId(json, arrow, segmentInitial) : null;
}

/** Id del elemento que mueve un evento (el balón en pases/tiros/botes; si no, sourceId). */
function resolveSubjectId(json: BoardJson, event: TimelineEvent, ballId: string | null): string | null {
  if (event.type === 'pass' || event.type === 'shot' || event.type === 'dribble') return ballId;
  return event.sourceId ?? null;
}

/** Segmento temporal de una concatenación: un gráfico con sus posiciones iniciales. */
interface AnimationSegment {
  /** Inicio absoluto del segmento en la línea de tiempo concatenada. */
  time: number;
  /** Fin absoluto (inicio del siguiente gráfico o duración total). */
  end: number;
  /** Posición inicial de cada elemento animable en este gráfico. */
  initial: Map<string, FramePosition>;
}

/**
 * Divide la línea de tiempo en segmentos (uno por gráfico) cuando el json trae
 * positionKeyframes (concatenación): cada gráfico evalúa con sus posiciones
 * guardadas y sus eventos, uno detrás de otro, sin mezclar los estados. Sin
 * keyframes hay un único segmento que cubre toda la línea de tiempo con las
 * posiciones del propio json (comportamiento clásico).
 */
function buildSegments(json: BoardJson, duration: number): AnimationSegment[] {
  const kfs = json.positionKeyframes;
  if (!kfs || kfs.length === 0) {
    return [{ time: 0, end: duration, initial: initialPositions(json) }];
  }
  const sorted = [...kfs].sort((a, b) => a.time - b.time);
  return sorted.map((kf, i) => ({
    time: kf.time,
    end: i + 1 < sorted.length ? sorted[i + 1].time : duration,
    initial: new Map(Object.entries(kf.positions).map(([id, pos]) => [id, { ...pos }])),
  }));
}

/** Segmento activo en el instante t: el último que ya ha arrancado. */
function activeSegment(segments: AnimationSegment[], t: number): AnimationSegment {
  let current = segments[0];
  for (const seg of segments) {
    if (seg.time <= t) current = seg;
    else break;
  }
  return current;
}

/**
 * Balón que mueve un bote: el balón del que arranca la flecha (sourceId) o,
 * si no, el balón cuya posición simulada en el instante del bote está más
 * cerca del inicio de la flecha. Se resuelve en runtime, sin usar el ballId
 * persistido (puede quedar obsoleto): el balón que acaba de llegar a un
 * jugador es el que lo acompaña al botar.
 */
function resolveDribbleBallId(json: BoardJson, event: TimelineEvent, segment: AnimationSegment, eventsBySubject: Map<string, TimelineEvent[]>, easing: EasingFn): string | null {
  const arrow = findArrow(json, event.arrowId);
  if (arrow?.sourceId && json.balls.some((b) => b.id === arrow.sourceId)) return arrow.sourceId;
  if (!arrow) return event.ballId && json.balls.some((b) => b.id === event.ballId) ? event.ballId : null;
  let closestId: string | null = null;
  let minDistSq = Infinity;
  for (const b of json.balls) {
    const initial = segment.initial.get(b.id);
    if (!initial) continue;
    const ballEvents = eventsBySubject.get(b.id) ?? [];
    const pos = simulateSubject(initial, ballEvents, event.startTime, json, b.id, easing, eventsBySubject, segment.initial).position;
    const dx = pos.x - arrow.x1;
    const dy = pos.y - arrow.y1;
    const d = dx * dx + dy * dy;
    if (d < minDistSq) {
      minDistSq = d;
      closestId = b.id;
    }
  }
  return closestId;
}

/**
 * Balón que mueve un pase/tiro: el balón del que arranca la flecha (sourceId) o,
 * si no, el balón cuya posición simulada en el instante del pase está más cerca
 * del PASADOR (sourceId si es persona) en ese mismo instante.
 * Se resuelve en runtime para que un pase/tiro posterior use el balón que virtualmente
 * llegó al pasador/tiro mediante un pase anterior, no el balón más cercano a la
 * posición inicial del pasador. El ballId persistido (de buildDefaultTimeline)
 * solo se usa como fallback final, ya que se calculó con posiciones iniciales
 * y puede quedar obsoleto tras pases previos.
 */
function resolvePassBallId(json: BoardJson, event: TimelineEvent, segment: AnimationSegment, eventsBySubject: Map<string, TimelineEvent[]>, easing: EasingFn): string | null {
  const arrow = findArrow(json, event.arrowId);
  // 1. Si la flecha arranca desde un balón (sourceId es un balón), usar ese balón.
  if (arrow?.sourceId && json.balls.some((b) => b.id === arrow.sourceId)) return arrow.sourceId;
  // 2. Si solo hay un balón, usar ese.
  if (json.balls.length === 1) return json.balls[0].id;
  if (!arrow) return null;

  // 3. Si la flecha tiene sourceId que es un jugador/defensor, buscar el balón
  //    más cercano a ESE jugador en el instante del pase/tiro (posición simulada).
  if (arrow.sourceId && isPerson(json, arrow.sourceId)) {
    // Simular posición del pasador/tiro en el instante del evento
    const passerInitial = segment.initial.get(arrow.sourceId);
    if (!passerInitial) return null;
    const passerEvents = eventsBySubject.get(arrow.sourceId) ?? [];
    const passerPos = simulateSubject(passerInitial, passerEvents, event.startTime, json, arrow.sourceId, easing, eventsBySubject, segment.initial).position;

    let closestId: string | null = null;
    let minDistSq = Infinity;
    for (const b of json.balls) {
      const initial = segment.initial.get(b.id);
      if (!initial) continue;
      const ballEvents = eventsBySubject.get(b.id) ?? [];
      const ballPos = simulateSubject(initial, ballEvents, event.startTime, json, b.id, easing, eventsBySubject, segment.initial).position;
      const dx = ballPos.x - passerPos.x;
      const dy = ballPos.y - passerPos.y;
      const d = dx * dx + dy * dy;
      if (d < minDistSq) {
        minDistSq = d;
        closestId = b.id;
      }
    }
    return closestId;
  }

  // 4. Fallback: balón más cercano al inicio geométrico de la flecha (posición simulada).
  let closestId: string | null = null;
  let minDistSq = Infinity;
  for (const b of json.balls) {
    const initial = segment.initial.get(b.id);
    if (!initial) continue;
    const ballEvents = eventsBySubject.get(b.id) ?? [];
    const ballPos = simulateSubject(initial, ballEvents, event.startTime, json, b.id, easing, eventsBySubject, segment.initial).position;
    const dx = ballPos.x - arrow.x1;
    const dy = ballPos.y - arrow.y1;
    const d = dx * dx + dy * dy;
    if (d < minDistSq) {
      minDistSq = d;
      closestId = b.id;
    }
  }
  // 5. Último recurso: ballId persistido (puede quedar obsoleto tras pases previos).
  if (event.ballId && json.balls.some((b) => b.id === event.ballId)) return event.ballId;
  return closestId;
}

/**
 * Jugador/defensor que conduce el balón en un bote: el sourceId de la flecha
 * si es un jugador/defensor; si el bote se dibujó desde el balón (o sin
 * enlazar), el jugador/defensor cuya posición simulada en el instante del
 * bote está más cerca de la posición del balón en ese instante (el receptor
 * acompaña el balón que acaba de llegar, aunque otro esté más cerca del
 * origen geométrico de la flecha).
 */
function resolveDribblerId(
  json: BoardJson,
  event: TimelineEvent,
  ballId: string | null,
  segment: AnimationSegment,
  eventsBySubject: Map<string, TimelineEvent[]>,
  easing: EasingFn,
): string | null {
  const sourceId = event.sourceId;
  const isPerson = (id: string): boolean => json.players.some((e) => e.id === id) || json.defenders.some((e) => e.id === id);
  // Si la flecha se dibujó desde un jugador/defensor, ese conduce el bote.
  if (sourceId && isPerson(sourceId)) return sourceId;
  // Posición del balón en el instante del bote (o el inicio de la flecha si
  // no hay balón): el conductor es la persona más próxima a ese punto.
  let ballPos: FramePosition | null = null;
  if (ballId) {
    const initial = segment.initial.get(ballId);
    if (initial) {
      ballPos = simulateSubject(initial, eventsBySubject.get(ballId) ?? [], event.startTime, json, ballId, easing, eventsBySubject, segment.initial).position;
    }
  }
  if (!ballPos) {
    const arrow = findArrow(json, event.arrowId);
    if (!arrow) return null;
    ballPos = { x: arrow.x1, y: arrow.y1 };
  }
  let closestId: string | null = null;
  let minDistSq = Infinity;
  for (const el of [...json.players, ...json.defenders]) {
    const initial = segment.initial.get(el.id);
    if (!initial) continue;
    const events = eventsBySubject.get(el.id) ?? [];
    const pos = simulateSubject(initial, events, event.startTime, json, el.id, easing, eventsBySubject, segment.initial).position;
    const dx = pos.x - ballPos.x;
    const dy = pos.y - ballPos.y;
    const d = dx * dx + dy * dy;
    if (d < minDistSq) {
      minDistSq = d;
      closestId = el.id;
    }
  }
  return closestId;
}

/**
 * Jugador/defensor que se mueve con una flecha de movimiento: si la flecha
 * tiene sourceId enlazado, usa ese jugador. Si no, busca el jugador/defensor
 * cuya posición simulada en el instante del evento está más cerca del inicio
 * de la flecha (x1, y1). Permite animar flechas de movimiento dibujadas cerca
 * de un jugador sin necesidad de enlazarlas explícitamente.
 */
function resolveMoveSubjectId(json: BoardJson, event: TimelineEvent, segment: AnimationSegment, eventsBySubject: Map<string, TimelineEvent[]>, easing: EasingFn): string | null {
  const sourceId = event.sourceId;
  const isPerson = (id: string): boolean => json.players.some((e) => e.id === id) || json.defenders.some((e) => e.id === id);
  // Si la flecha se dibujó desde un jugador/defensor enlazado, ese se mueve.
  if (sourceId && isPerson(sourceId)) return sourceId;

  const arrow = findArrow(json, event.arrowId);
  if (!arrow) return null;

  // Punto de referencia: inicio de la flecha de movimiento.
  const refPos = { x: arrow.x1, y: arrow.y1 };

  let closestId: string | null = null;
  let minDistSq = Infinity;
  for (const el of [...json.players, ...json.defenders]) {
    const initial = segment.initial.get(el.id);
    if (!initial) continue;
    const events = eventsBySubject.get(el.id) ?? [];
    const pos = simulateSubject(initial, events, event.startTime, json, el.id, easing, eventsBySubject, segment.initial).position;
    const dx = pos.x - refPos.x;
    const dy = pos.y - refPos.y;
    const d = dx * dx + dy * dy;
    if (d < minDistSq) {
      minDistSq = d;
      closestId = el.id;
    }
  }
  return closestId;
}

/**
 * Centro del aro al que se dirige un tiro: en cancha completa, el aro más
 * cercano al final de la flecha; en el resto de modos, el único aro de la
 * cancha. El balón de un tiro viaja en línea recta hasta este punto, sin
 * seguir la trayectoria dibujada de la flecha.
 */
function resolveShotTarget(json: BoardJson, arrow: BoardArrow): FramePosition {
  const rims = Court.getRimCenters(json.courtMode);
  if (rims.length === 1) return rims[0];
  let best = rims[0];
  let minDistSq = Infinity;
  for (const rim of rims) {
    const dx = rim.x - arrow.x2;
    const dy = rim.y - arrow.y2;
    const d = dx * dx + dy * dy;
    if (d < minDistSq) {
      minDistSq = d;
      best = rim;
    }
  }
  return best;
}

/** Duración total de la línea de tiempo (la declarada o la derivada de los eventos). */
function resolveDuration(timeline: BoardTimeline): number {
  if (timeline.duration > 0) return timeline.duration;
  return timeline.events.reduce((max, e) => Math.max(max, e.startTime + e.duration), 0);
}

/**
 * Simula la posición de un elemento a lo largo de sus eventos (ordenados por
 * startTime). Encadena movimientos: la posición previa es el punto de partida
 * del siguiente evento. En caso de solape, gana el evento que arrancó antes.
 * También devuelve la rotación (en radianes) para movimientos/bloqueos que
 * siguen una flecha, para que el elemento "mire" en la dirección del movimiento.
 */
function simulateSubject(
  initial: FramePosition,
  events: TimelineEvent[],
  t: number,
  json: BoardJson,
  subjectId: string,
  easing: EasingFn,
  eventsBySubject?: Map<string, TimelineEvent[]>,
  segmentInitial?: Map<string, FramePosition>,
): { position: FramePosition; rotation?: number; active?: TimelineEvent } {
  let position = { ...initial };
  let rotation: number | undefined;
  let active: TimelineEvent | undefined;

  // Ordena por startTime: la simulación asume eventos cronológicos y en caso
  // de solape gana el que arrancó antes. Los callers (botes, resolución de
  // balón/conductor) pasan listas sin ordenar según el orden de inserción.
  const sorted = [...events].sort((a, b) => a.startTime - b.startTime);
  for (const event of sorted) {
    const start = event.startTime;
    const end = start + event.duration;
    if (t < start) break; // los eventos posteriores aún no arrancan

    if (event.type === 'hold') {
      // Mantiene la posición actual durante su duración.
      if (t < end) {
        active = event;
        break;
      }
      continue;
    }

    const arrow = findArrow(json, event.arrowId);
    if (!arrow) continue;

    if (t < end) {
      // Evento activo: interpolar sobre la trayectoria de la flecha.
      const local = end > start ? (t - start) / (end - start) : 1;
      // Los movimientos y bloqueos aceleran y deceleran de forma natural
      // (ease-in-out); el balón y los botes conservan el easing configurado.
      const isMovement = event.type === 'move' || event.type === 'block';
      const p = isMovement ? easeInOut(local) : easing(local);
      // Punto de referencia: el elemento recorre la trayectoria de la flecha.
      const ref = pointOnArrow(arrow, p);
      if (isMovement) {
        // Jugadores, defensores y entrenador siguen EXACTAMENTE la trayectoria
        // de la flecha pintada: la posición es el punto sobre la flecha, sin
        // desviaciones por la posición previa del elemento.
        position = ref;
        // Rotación: tangente de la flecha en el progreso actual (ease-in-out).
        // Solo si está habilitado (configurable via ENABLE_PLAYER_ROTATION_DURING_PLAYBACK).
        if (ENABLE_PLAYER_ROTATION_DURING_PLAYBACK) {
          rotation = angleOnArrow(arrow, p);
        }
      } else if (event.type === 'shot') {
        // Tiro: el balón viaja en línea recta desde su posición actual hasta
        // el centro del aro, sin seguir la trayectoria dibujada de la flecha.
        const rim = resolveShotTarget(json, arrow);
        position = {
          x: lerp(position.x, rim.x, p),
          y: lerp(position.y, rim.y, p),
        };
      } else {
        // El balón (pases/botes): si no estaba exactamente en el inicio
        // de la flecha, se desliza suavemente desde su posición actual hasta
        // el final de la flecha siguiendo su forma (evita saltos, p. ej. el
        // balón colocado lejos del pasador).
        const fade = 1 - p;
        position = {
          x: ref.x + (position.x - arrow.x1) * fade,
          y: ref.y + (position.y - arrow.y1) * fade,
        };
      }
      active = event;
      break;
    }

    // Evento ya terminado: el elemento queda al final de la flecha.
    // El tiro termina siempre en el centro del aro (no tiene receptor).
    if (event.type === 'shot') {
      position = resolveShotTarget(json, arrow);
    } else if (event.type === 'pass' && arrow.targetId && eventsBySubject) {
      // Para pases, si la flecha tiene targetId, el balón se pega al
      // receptor (simula la posición del receptor en el instante final del pase).
      // El balón se coloca en el borde del círculo del jugador/defensor,
      // no en el centro, para que no quede "encima" del jugador.
      const targetEvents = eventsBySubject.get(arrow.targetId) ?? [];
      const targetInitial = (segmentInitial ?? initialPositions(json)).get(arrow.targetId);
      if (targetInitial) {
        const targetResult = simulateSubject(targetInitial, targetEvents, end, json, arrow.targetId, easing, eventsBySubject, segmentInitial);
        const targetPos = targetResult.position;
        // Dirección desde el centro del receptor hacia el final de la flecha
        // (donde apunta la flecha de pase).
        const dx = arrow.x2 - targetPos.x;
        const dy = arrow.y2 - targetPos.y;
        const dist = Math.hypot(dx, dy) || 1;
        // Radio del receptor según su tipo (jugador o defensor).
        const isPlayer = json.players.some((p) => p.id === arrow.targetId);
        const receptorRadius = isPlayer ? PLAYER_RADIUS : DEFENDER_RADIUS;
        // El balón se coloca a (radio_receptor + radio_balón) del centro,
        // en la dirección de la flecha.
        const offset = receptorRadius + BALL_RADIUS;
        position = {
          x: targetPos.x + (dx / dist) * offset,
          y: targetPos.y + (dy / dist) * offset,
        };
      } else {
        position = { x: arrow.x2, y: arrow.y2 };
      }
    } else {
      position = { x: arrow.x2, y: arrow.y2 };
    }
    // Para movimientos/bloqueos ya terminados, la rotación es la tangente al final de la flecha.
    if (ENABLE_PLAYER_ROTATION_DURING_PLAYBACK && (event.type === 'move' || event.type === 'block')) {
      rotation = angleOnArrow(arrow, 1);
    }
  }

  return { position, rotation, active };
}

/**
 * Evalúa la animación en un instante t y devuelve la posición de cada elemento.
 * En una concatenación (positionKeyframes) cada gráfico evalúa su propia
 * animación con sus posiciones guardadas y sus eventos, una detrás de otra;
 * sin keyframes los elementos sin eventos mantienen su posición inicial del
 * BoardJson.
 */
export function evaluateAnimation(json: BoardJson, timeline: BoardTimeline, time: number, options: AnimationOptions = {}): BoardFrame {
  const duration = resolveDuration(timeline);
  const t = Math.min(Math.max(time, 0), duration);
  const progress = duration > 0 ? t / duration : 0;
  const easing = options.easing ?? linear;

  // Segmento activo: en una concatenación, el gráfico en curso (con sus
  // posiciones guardadas); sin positionKeyframes hay un único segmento.
  const segments = buildSegments(json, duration);
  const segment = activeSegment(segments, t);
  const segmentEvents = timeline.events.filter((ev) => ev.startTime >= segment.time && ev.startTime < segment.end);

  // Posiciones iniciales del gráfico activo: los elementos sin eventos en él
  // permanecen donde se guardaron en ese gráfico.
  const positions = new Map<string, FramePosition>();
  for (const [id, pos] of segment.initial) positions.set(id, { ...pos });

  // Rotaciones: se pueblan desde la simulación (movimientos/bloques/botes)
  // y desde rotationKeyframes (concatenaciones).
  const rotations = new Map<string, number>();

  // Agrupar eventos por elemento afectado para poder encadenar movimientos.
  const eventsBySubject = new Map<string, TimelineEvent[]>();
  const addToSubject = (subjectId: string | null, event: TimelineEvent): void => {
    if (!subjectId || !positions.has(subjectId)) return;
    const list = eventsBySubject.get(subjectId);
    if (list) list.push(event);
    else eventsBySubject.set(subjectId, [event]);
  };

  // Primero los eventos que no son botes (pases/tiros/movimientos/bloqueos):
  // alimentan la resolución dinámica de los botes (balón y conductor). Los
  // eventos de rol e indicación no mueven ningún elemento: se registran como
  // activos más abajo (el playback muestra la flecha de indicación durante su
  // intervalo) y aquí se ignoran.
  const dribbleEvents: TimelineEvent[] = [];
  for (const event of segmentEvents) {
    if (event.type === 'dribble') {
      dribbleEvents.push(event);
      continue;
    }
    if (event.type === 'role' || event.type === 'indication') continue;
    // Para pases/tiros, resolver el balón dinámicamente usando posiciones
    // simuladas en el instante del evento (el balón que virtualmente llegó
    // al pasador). Para movimientos sin sourceId, resolver el jugador más
    // cercano al inicio de la flecha en el instante del evento.
    let subjectId: string | null = null;
    if (event.type === 'pass' || event.type === 'shot') {
      const ballId = resolvePassBallId(json, event, segment, eventsBySubject, easing);
      subjectId = resolveSubjectId(json, event, ballId);
    } else if (event.type === 'move' && !event.sourceId) {
      // Movimiento sin sourceId enlazado: buscar jugador más cercano al inicio de la flecha
      subjectId = resolveMoveSubjectId(json, event, segment, eventsBySubject, easing);
    } else {
      const ballId = resolveBallId(json, event, segment.initial);
      subjectId = resolveSubjectId(json, event, ballId);
    }
    addToSubject(subjectId, event);
  }

  // Después los botes: el balón y el conductor se resuelven en runtime con la
  // posición simulada en el instante del bote (el balón que acaba de llegar
  // acompaña al jugador más próximo a él). Si no hay conductor, el balón
  // recorre la flecha por sí solo (comportamiento anterior).
  const dribblesByBall = new Map<string, TimelineEvent[]>();
  const dribblerByEvent = new Map<string, string>();
  for (const event of dribbleEvents) {
    const ballId = resolveDribbleBallId(json, event, segment, eventsBySubject, easing);
    const dribblerId = resolveDribblerId(json, event, ballId, segment, eventsBySubject, easing);
    if (dribblerId) {
      addToSubject(dribblerId, event);
      if (ballId && positions.has(ballId)) {
        const list = dribblesByBall.get(ballId);
        if (list) list.push(event);
        else dribblesByBall.set(ballId, [event]);
        dribblerByEvent.set(event.id, dribblerId);
      }
    } else if (ballId) {
      addToSubject(ballId, event);
    }
  }

  const activeEvents: TimelineEvent[] = [];
  const seenActive = new Set<string>();
  for (const [subjectId, events] of eventsBySubject) {
    const sorted = [...events].sort((a, b) => a.startTime - b.startTime);
    const initial = positions.get(subjectId)!;
    const result = simulateSubject(initial, sorted, t, json, subjectId, easing, eventsBySubject, segment.initial);
    positions.set(subjectId, result.position);
    // Si la simulación devuelve rotación (movimientos/bloqueos siguiendo flecha), guardarla.
    if (result.rotation !== undefined) {
      rotations.set(subjectId, result.rotation);
    }
    if (result.active && !seenActive.has(result.active.id)) {
      seenActive.add(result.active.id);
      activeEvents.push(result.active);
    }
  }

  // El balón de un bote sigue al jugador que lo conduce manteniendo el
  // desplazamiento que tenía al empezar el bote (se mueven como una unidad).
  // Se calcula al final porque depende de la posición del jugador en t.
  for (const [ballId, dribbles] of dribblesByBall) {
    const ballEvents = eventsBySubject.get(ballId) ?? [];
    const ballInitial = segment.initial.get(ballId);
    if (!ballInitial) continue;
    const sorted = [...dribbles].sort((a, b) => a.startTime - b.startTime);
    // Posición del balón al empezar el primer bote (tras sus pases previos);
    // los botes encadenados heredan la posición del bote anterior.
    let ballPos = simulateSubject(ballInitial, ballEvents, sorted[0].startTime, json, ballId, easing, eventsBySubject, segment.initial).position;
    let lastDribbleEnd = -Infinity;
    for (let i = 0; i < sorted.length; i++) {
      const event = sorted[i];
      const start = event.startTime;
      const end = start + event.duration;
      if (t < start) {
        // Aún no arranca este bote: si uno anterior ya terminó, el balón
        // sigue sus eventos intermedios (pases) desde donde lo dejó el último
        // bote, en vez de quedarse pegado a él (p. ej. un mano a mano entre
        // dos botes: el balón debe recorrer el pase hasta el receptor).
        if (lastDribbleEnd > -Infinity) {
          const gapEvents = ballEvents.filter((e) => e.startTime >= lastDribbleEnd && e.startTime < start);
          const gapPos = simulateSubject(ballPos, gapEvents, t, json, ballId, easing, eventsBySubject, segment.initial).position;
          positions.set(ballId, gapPos);
        }
        break;
      }
      const dribblerId = dribblerByEvent.get(event.id);
      if (!dribblerId) continue;
      const dribblerEvents = eventsBySubject.get(dribblerId) ?? [];
      const dribblerInitial = segment.initial.get(dribblerId);
      if (!dribblerInitial) continue;
      // Si hay un hueco entre el bote anterior y este (p. ej. un pase), el
      // balón recorre esos eventos desde donde lo dejó el bote anterior hasta
      // el inicio de este bote; ese punto es la base del desplazamiento.
      if (lastDribbleEnd > -Infinity && start > lastDribbleEnd) {
        const gapEvents = ballEvents.filter((e) => e.startTime >= lastDribbleEnd && e.startTime < start);
        ballPos = simulateSubject(ballPos, gapEvents, start, json, ballId, easing, eventsBySubject, segment.initial).position;
      }
      // Desplazamiento del balón respecto al jugador al empezar el bote.
      const dribblerAtStart = simulateSubject(dribblerInitial, dribblerEvents, start, json, dribblerId, easing, eventsBySubject, segment.initial).position;
      const offset = { x: ballPos.x - dribblerAtStart.x, y: ballPos.y - dribblerAtStart.y };
      if (t < end) {
        // Bote activo: el balón acompaña al jugador en el instante t.
        const dribblerResult = simulateSubject(dribblerInitial, dribblerEvents, t, json, dribblerId, easing, eventsBySubject, segment.initial);
        positions.set(ballId, { x: dribblerResult.position.x + offset.x, y: dribblerResult.position.y + offset.y });
        // Rotación del conductor del bote: tangente de la flecha de bote en el progreso actual (ease-in-out).
        // Solo si está habilitado (configurable via ENABLE_PLAYER_ROTATION_DURING_PLAYBACK).
        if (ENABLE_PLAYER_ROTATION_DURING_PLAYBACK) {
          const arrow = findArrow(json, event.arrowId);
          if (arrow) {
            const local = (t - start) / (end - start);
            const p = easeInOut(local);
            rotations.set(dribblerId, angleOnArrow(arrow, p));
          }
        }
        break;
      }
      // Bote terminado: el balón queda donde lo deja el jugador al terminar;
      // ese punto lo hereda el siguiente bote (o los pases posteriores).
      const dribblerAtEnd = simulateSubject(dribblerInitial, dribblerEvents, end, json, dribblerId, easing, eventsBySubject, segment.initial).position;
      ballPos = { x: dribblerAtEnd.x + offset.x, y: dribblerAtEnd.y + offset.y };
      lastDribbleEnd = end;
      // Si era el último bote, el balón retoma sus siguientes eventos (pases)
      // desde donde lo dejó el jugador, en vez de quedar pegado a él.
      if (i === sorted.length - 1) {
        const afterDribbles = ballEvents.filter((e) => e.startTime >= end);
        const finalPos = simulateSubject(ballPos, afterDribbles, t, json, ballId, easing, eventsBySubject, segment.initial).position;
        positions.set(ballId, finalPos);
      } else if (t >= end) {
        // Bote intermedio ya terminado: si estamos después de su final (o justo
        // en el final), colocar el balón donde lo dejó antes de continuar.
        positions.set(ballId, ballPos);
      }
    }
  }
  activeEvents.sort((a, b) => a.startTime - b.startTime);

  // Eventos de rol e indicación activos: no mueven elementos, solo se registran
  // para que el playback muestre la flecha de indicación durante su intervalo
  // (la visibilidad de flechas la gestiona updateArrowsVisibility por arrowId).
  for (const event of segmentEvents) {
    if ((event.type === 'role' || event.type === 'indication') && t >= event.startTime && t < event.startTime + event.duration) {
      activeEvents.push(event);
    }
  }
  activeEvents.sort((a, b) => a.startTime - b.startTime);

  // Roles por elemento: rol base (jugador/defensor según la lista del json) +
  // eventos de tipo 'role' con startTime <= t (el último gana; el cambio es
  // instantáneo en el instante del evento). En una concatenación los eventos
  // llevan offsets, así que los roles se evalúan cronológicamente entre gráficos.
  const roles = new Map<string, 'player' | 'defender'>();
  for (const p of json.players) roles.set(p.id, 'player');
  for (const d of json.defenders) roles.set(d.id, 'defender');
  for (const event of timeline.events) {
    if (event.type === 'role' && event.subjectId && event.startTime <= t && (event.toRole === 'player' || event.toRole === 'defender')) {
      roles.set(event.subjectId, event.toRole);
    }
  }

  // Rotaciones por elemento: en una concatenación (rotationKeyframes) cada
  // gráfico fija el ángulo de sus jugadores/defensores al arrancar. Se elige
  // el último keyframe con time <= t (concatGraphics los genera ordenados);
  // los elementos sin keyframe conservan su ángulo base (el del primer gráfico).
  // Nota: el mapa rotations ya se creó al inicio y se pobló con rotaciones de
  // simulación (movimientos/bloques/botes). Aquí añadimos/actualizamos con
  // rotationKeyframes (concatenaciones), que tienen prioridad.
  if (json.rotationKeyframes && json.rotationKeyframes.length > 0) {
    let current = json.rotationKeyframes[0];
    for (const kf of json.rotationKeyframes) {
      if (kf.time <= t) current = kf;
      else break;
    }
    for (const [id, angle] of Object.entries(current.playerAngles)) rotations.set(id, angle);
    for (const [id, angle] of Object.entries(current.defenderAngles)) rotations.set(id, angle);
  }

  // Visibilidad de elementos: en una concatenación (elementVisibility) cada
  // gráfico fija los elementos que se muestran al arrancar (los añadidos o
  // borrados entre gráficos solo aparecen en su parte). Mismo criterio que las
  // rotaciones: último keyframe con time <= t.
  const visibleElements = new Set<string>();
  if (json.elementVisibility && json.elementVisibility.length > 0) {
    let current = json.elementVisibility[0];
    for (const kf of json.elementVisibility) {
      if (kf.time <= t) current = kf;
      else break;
    }
    for (const id of current.visibleIds) visibleElements.add(id);
  }

  return { positions, rotations, visibleElements, roles, progress, activeEvents };
}

/** Duración por defecto (unidades de tiempo) de cada evento en la línea de tiempo automática. */
export const DEFAULT_EVENT_DURATION = 1.2;

/**
 * Duración del "clip inicial": una breve pausa sin flechas al comienzo de la
 * animación para mostrar la disposición del tablero antes de la primera acción.
 */
export const INTRO_DURATION = 0.4;

/** Id del evento interno del clip inicial (pausa sin flechas). No se muestra en el editor ni se puede editar. */
export const INTRO_EVENT_ID = 'ev_intro';

/** Flecha animable junto con el tipo de evento que genera. */
export interface AnimatableArrow {
  arrow: BoardArrow;
  type: TimelineEventType;
}

/**
 * Tipo de evento que genera una flecha, o null si no es animable: las flechas
 * marcadas como indicación (indication: true) no ejecutan ninguna acción y los
 * bloqueos sin enlazar a un origen no pueden animar a nadie. Las flechas de
 * movimiento sin sourceId sí se animan: el sujeto se resuelve en runtime.
 * Fuente única para `animatableArrows` y para añadir automáticamente el evento
 * al dibujar la flecha (sin pasar por el editor de la línea de tiempo).
 */
export function arrowEventType(arrow: BoardArrow): TimelineEventType | null {
  if (arrow.indication) return null;
  switch (arrow.type) {
    case 'pass-arrow':
      return 'pass';
    case 'shot-arrow':
      return 'shot';
    case 'dribble-arrow':
    case 'curved-dribble-arrow':
      return 'dribble';
    case 'arrow':
    case 'curved-arrow':
      // Flechas de movimiento: si tienen sourceId enlazado, usan ese jugador.
      // Si no, se resuelve dinámicamente en runtime buscando el jugador/defensor
      // más cercano al inicio de la flecha (igual que los botes desde balón).
      return 'move';
    case 'block-arrow':
    case 'curved-block-arrow':
      // Las flechas de bloqueo enlazadas generan un evento de tipo 'block'.
      return arrow.sourceId ? 'block' : null;
    default:
      return null;
  }
}

/**
 * Devuelve las flechas del tablero que pueden generar eventos de animación,
 * ordenadas por el orden de dibujo (order). Los gráficos antiguos sin order
 * conservan el orden por categorías (pases → tiros → botes → movimientos → bloqueos).
 */
export function animatableArrows(json: BoardJson): AnimatableArrow[] {
  const result: AnimatableArrow[] = [];
  for (const key of ARROW_KEYS) {
    for (const arrow of json[key] as BoardArrow[]) {
      const type = arrowEventType(arrow);
      if (type) result.push({ arrow, type });
    }
  }
  result.sort((a, b) => (a.arrow.order ?? Number.MAX_SAFE_INTEGER) - (b.arrow.order ?? Number.MAX_SAFE_INTEGER));
  return result;
}

/**
 * Genera una línea de tiempo por defecto a partir de las flechas enlazadas del
 * tablero: cada flecha animable se convierte en un evento en secuencia.
 * Devuelve null si no hay ninguna flecha animable (no hay nada que reproducir).
 * El editor de timeline (Fase 2) permitirá personalizar esta línea de tiempo.
 */
export function buildDefaultTimeline(json: BoardJson): BoardTimeline | null {
  const events: TimelineEvent[] = [];
  // Las acciones arrancan en el 0s de la línea de tiempo: sin clip previo, la
  // duración editable es la de los propios eventos (la pausa inicial de 0.4s
  // ya no forma parte de la duración).
  let time = 0;
  const jsonPositions = initialPositions(json);
  const add = (type: TimelineEventType, arrow: BoardArrow): void => {
    // En pases/tiros/botes se fija el balón que mueve la flecha: con varios
    // balones en el tablero, sin esto la animación no sabría cuál mover. En
    // runtime los botes re-resuelven el balón dinámicamente (el que acaba de
    // llegar al jugador); el valor guardado es la referencia para pases/tiros.
    const isBallEvent = type === 'pass' || type === 'shot' || type === 'dribble';
    events.push({
      id: `ev_${events.length + 1}`,
      type,
      arrowId: arrow.id,
      startTime: time,
      duration: DEFAULT_EVENT_DURATION,
      sourceId: arrow.sourceId,
      targetId: arrow.targetId,
      ballId: isBallEvent ? resolveArrowBallId(json, arrow, jsonPositions) : undefined,
    });
    time += DEFAULT_EVENT_DURATION;
  };

  // Reutiliza animatableArrows (fuente única de recolección y orden): la línea
  // de tiempo por defecto respeta el orden en que el usuario pintó las flechas.
  const candidates = animatableArrows(json);
  candidates.forEach((c) => add(c.type, c.arrow));

  if (events.length === 0) return null;
  return { duration: time, events };
}
